import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '../../features/auth/usePermissions';
import { NAV_GROUPS, dublZarplaty, razdelRazreshen, type NavSection } from './menyuAdminki';
import { Ctx, activeSectionKey, type RazdelyContextValue } from './razdely';
import { menyuPamyat } from './menyuPamyat';

/**
 * ⛔ ПРОВАЙДЕР ЖИВЁТ ОТДЕЛЬНО ОТ ХУКА, И ЭТО НЕ ПРИДИРКА ЛИНТЕРА. Файл, где
 * рядом стоят компонент и обычная функция, ломает горячую перезагрузку: правка
 * хука перезагружает модуль целиком и роняет состояние всего каркаса. Ядро —
 * контекст, тип и разбор маршрута — в `razdely.ts`; здесь только компонент.
 */
export function RazdelyProvider({ children }: { children: ReactNode }) {
  const { can, isSuperAdmin, roles = [], sectionHidden } = usePermissions();
  const { pathname } = useLocation();

  const tekushchiyKey = useMemo(() => activeSectionKey(pathname), [pathname]);

  // `undefined` — «за меня решает маршрут»; строка или null — решил владелец.
  /*
   * ⛔ `s713` Д-08: выбор раздела и раскрытый пункт живут в `menyuPamyat`, а не в состоянии
   * каркаса — каркас пересоздаётся на каждом переходе и раньше забывал их.
   */
  const { gruppa: openGroup, punkt: openSection } = useSyncExternalStore(menyuPamyat.podpisatsya, menyuPamyat.snimok);

  const vidimyy = (s: NavSection) =>
    razdelRazreshen(s.permission, can) &&
    (!s.superAdmin || isSuperAdmin) &&
    ! sectionHidden(s.key) &&
    ! dublZarplaty(s.key, can) &&
    (!s.hiddenForRoles || !s.hiddenForRoles.some((r) => roles.includes(r)));

  const tekushchayaGruppa = useMemo(
    () => NAV_GROUPS.find((g) => g.sections.some((x) => x.key === tekushchiyKey))?.label,
    [tekushchiyKey],
  );

  /**
   * ⛔ ПО УМОЛЧАНИЮ — ТОТ РАЗДЕЛ, ГДЕ ВЛАДЕЛЕЦ НАХОДИТСЯ, а не первый по списку.
   * Маршрут знает это точнее запомненного клика; открывшись «Аналитикой» на
   * экране записи, каркас каждый раз просил бы переключить себя обратно.
   */
  const gruppy = NAV_GROUPS.filter((g) => g.sections.filter(vidimyy).length > 0).map((g) => g.label);
  const zapomnennaya = openGroup !== undefined && gruppy.includes(openGroup) ? openGroup : undefined;
  const razdelVkladki = menyuPamyat.razdelVkladki();

  // Порядок: выбор человека → раздел по адресу → последний раздел вкладки → первый видимый.
  const vybrannaya =
    zapomnennaya
    ?? tekushchayaGruppa
    ?? (razdelVkladki !== null && gruppy.includes(razdelVkladki) ? razdelVkladki : undefined)
    ?? gruppy[0]
    ?? '';

  /*
   * Страница открыта заново, и адрес назвал раздел — он и запоминается. Дальше переходы по
   * рабочей области раздел и раскрытый пункт не двигают: это делает только нажатие в меню.
   */
  useEffect(() => {
    if (openGroup === undefined && tekushchayaGruppa !== undefined) {
      menyuPamyat.zapomnit({ gruppa: tekushchayaGruppa, punkt: openSection === undefined ? tekushchiyKey : openSection });
    }
  }, [openGroup, openSection, tekushchayaGruppa, tekushchiyKey]);

  const znachenie: RazdelyContextValue = {
    gruppy,
    vybrannaya,
    vybrat: (label) => {
      // Другой раздел — свои пункты: раскрытие по адресу, прокрутка сверху.
      menyuPamyat.zapomnitProkrutku(0);
      menyuPamyat.zapomnit({ gruppa: label, punkt: undefined });
    },
    vidimyy,
    tekushchiyKey,
    otkrytyyPunkt: openSection,
    raskryt: (key) => menyuPamyat.zapomnit({ punkt: key }),
  };

  return <Ctx.Provider value={znachenie}>{children}</Ctx.Provider>;
}

